import { Pin } from 'lucide-react';
import { PageHeader } from '@gs/ui';
import { useAuth } from '@gs/auth';
import type { AppDefinition } from '../types';
import { useFavorites } from '../hooks/useFavorites';
import { usePins } from '../hooks/usePins';
import { getAppById } from '../data/apps';
import { EmptyFavorites } from '../components/EmptyFavorites';
import { AppGrid } from '../components/AppGrid';
import { PinnedCard } from '../components/PinnedCard';
import { ZoneHeader } from '../components/ZoneHeader';
import { useTranslation } from '../i18n';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { favs, toggle, isFav, reorder } = useFavorites();
  const { pins, togglePin, isPinned, canPin } = usePins();

  // Apps épinglées (dans l'ordre des pins)
  const pinnedApps: AppDefinition[] = pins
    .map((id) => getAppById(id))
    .filter((a): a is AppDefinition => a != null);

  // Favoris non-épinglés (grille normale)
  const favApps: AppDefinition[] = favs
    .map((id) => getAppById(id))
    .filter((a): a is AppDefinition => a != null && !isPinned(a.id));

  const hasAnything = pinnedApps.length > 0 || favApps.length > 0;

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title={t('hub:dashboard.title')}
        description={t('hub:dashboard.description')}
      />

      {!hasAnything ? (
        <EmptyFavorites />
      ) : (
        <>
          {/* ── Section Accès rapide (épinglées) ────────────────────── */}
          {pinnedApps.length > 0 && (
            <section aria-label={t('hub:dashboard.pinnedAria')}>
              <ZoneHeader
                icon={<Pin size={14} />}
                title={t('hub:dashboard.pinnedTitle')}
                subtitle={t('hub:dashboard.pinnedCount', { n: pinnedApps.length })}
                accentColor="brand"
              />
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(112px,132px))]">
                {pinnedApps.map((app) => (
                  <PinnedCard key={app.id} app={app} userEmail={user?.email} onTogglePin={togglePin} />
                ))}
              </div>
            </section>
          )}

          {/* ── Section Mes favoris (non-épinglés) ──────────────────── */}
          {favApps.length > 0 && (
            <section aria-label={t('hub:dashboard.favoritesAria')}>
              {pinnedApps.length > 0 && (
                <ZoneHeader icon={<span className="text-sm">★</span>} title={t('hub:dashboard.favoritesTitle')} accentColor="slate" />
              )}
              <div className="group/grid relative">
                <AppGrid
                  userEmail={user?.email}
                  apps={favApps}
                  showStar={false}
                  isFav={isFav}
                  onToggleFav={toggle}
                  onReorder={reorder}
                  onTogglePin={togglePin}
                  isPinned={isPinned}
                  canPin={canPin}
                />
              </div>
              {favApps.length > 1 && (
                <p className="mt-2 text-center text-meta text-slate-400 dark:text-slate-600">
                  {t('hub:dashboard.dragHint')}
                </p>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
